import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Truck, Calendar, Package, Plus, Wrench, ArrowDownCircle, ArrowUpCircle, PlusCircle, ClipboardList, MapPin, Save, FileText, AlertTriangle, XCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Skeleton } from '../../components/ui/Skeleton';
import { useDespachos, useDespachoDetail, useUpdateDespachoItems } from '../../hooks/useDespachos';
import { useOrdenMaterialesPdf, useAllPedidos, useCreatePedido, useCancelPedido } from '../../hooks/usePedidos';
import { useMaterials } from '../../hooks/useMaterials';
import { api } from '../../lib/api-client';
import { useEquipos, useCreateEquipo } from '../../hooks/useEquipos';
import { useEquipoMovimientos, useCreateMovimiento, useEquipoUbicaciones } from '../../hooks/useEquipoMovimientos';
import type { UbicacionItem } from '../../hooks/useEquipoMovimientos';
import { useProjects } from '../../hooks/useProjects';
import { useAuthStore } from '../../stores/auth-store';
import { toast } from '../../components/ui/Toast';

type TabKey = 'pedidos' | 'inventario';

export function DespachosPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('pedidos');
  const [projectFilter] = useState<string | undefined>();
  const { data, isLoading, error } = useDespachos({ projectId: projectFilter });

  const { data: pendingPedidos } = useAllPedidos({ estado: 'pendiente' });
  const { data: parcialPedidos } = useAllPedidos({ estado: 'despachado_parcial' });

  const [expandedDespachoId, setExpandedDespachoId] = useState<string | null>(null);
  const [showExcedenteModal, setShowExcedenteModal] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelEstado, setCancelEstado] = useState<string | null>(null);
  const cancelPedido = useCancelPedido();
  const { user } = useAuthStore();

  const handleCancelPedido = async () => {
    if (!cancelId) return;
    try {
      await cancelPedido.mutateAsync({ pedidoId: cancelId });
      toast('success', 'Pedido cancelado');
      setCancelId(null);
      setCancelEstado(null);
    } catch {
      toast('error', 'Error al cancelar el pedido');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 fade-up p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600 mb-2">Error al cargar despachos</p>
        <Button variant="secondary" onClick={() => window.location.reload()}>Reintentar</Button>
      </div>
    );
  }

  const despachosList = data?.data ?? [];

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-dark flex items-center gap-2">
          <Truck className="w-6 h-6" />
          Compras
        </h1>
      </div>

      {/* Tabs (C-37) */}
      <div className="flex gap-1 bg-white/40 rounded-lg p-1">
        {([
          { key: 'pedidos' as TabKey, label: 'Materiales y Equipos' },
          { key: 'inventario' as TabKey, label: 'Inventario' },
        ]).map((tab) => (
          <button
            key={tab.key}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${
              activeTab === tab.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'pedidos' && (
        <>
          {/* All pending pedidos */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-brand-dark">Pedidos Pendientes de Despacho</h3>
              {['gerente', 'operaciones', 'administrativo', 'compras'].includes(user?.role ?? '') && (
                <Button size="sm" variant="secondary" onClick={() => setShowExcedenteModal(true)}>
                  <AlertTriangle className="w-3.5 h-3.5 mr-1 text-amber-500" />
                  Pedido Excedente
                </Button>
              )}
            </div>
            {(() => {
              const allPending = [...(pendingPedidos?.data ?? []), ...(parcialPedidos?.data ?? [])];
              if (allPending.length === 0) {
                return <p className="text-xs text-gray-400 py-4 text-center">No hay pedidos pendientes</p>;
              }
              return (
                <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                  {allPending.map((p) => (
                    <div key={p.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-3 hover:bg-gray-50/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">
                          {p.project_nombre} — <span className="text-blue-600">Pedido #{p.numero_orden != null ? p.numero_orden : p.numero}</span>
                        </p>
                        <p className="text-xs text-gray-400">
                          {p.project_codigo} &middot; {p.tipo} &middot; {new Date(p.created_at).toLocaleDateString('es-EC')} &middot; {p.created_by.nombre}
                        </p>
                        {p.notas && <p className="text-xs text-gray-500 mt-1 italic">{p.notas}</p>}
                      </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        p.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {p.estado === 'pendiente' ? 'Pendiente' : 'Despachado'}
                      </span>
                      <Button size="sm" variant="primary" onClick={() => navigate(`/compras/pedidos/${p.id}`)}>
                        <ClipboardList className="w-4 h-4 mr-1" />
                        Ver pedido
                      </Button>
                      <button
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Cancelar pedido"
                        onClick={(e) => { e.stopPropagation(); setCancelId(p.id); setCancelEstado(p.estado); }}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Card>

          {/* Despachos history */}
          {despachosList.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Historial de Despachos</p>
              <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
              {despachosList.map((d) => {
                const isExpanded = expandedDespachoId === d.id;
                return (
                  <Card key={d.id} className="p-4">
                    <div
                      className="flex items-start justify-between cursor-pointer"
                      onClick={() => setExpandedDespachoId(isExpanded ? null : d.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">
                          {d.project_nombre}
                          {d.pedido_numero_orden != null && (
                            <span className="ml-1.5 text-xs font-normal text-gray-400">#{d.pedido_numero_orden}</span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>{new Date(d.fecha_despacho).toLocaleDateString('es-EC')}</span>
                          <span>&middot;</span>
                          <Package className="w-3 h-3" />
                          <span>{d.items_count} ítem{d.items_count !== 1 ? 's' : ''}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{d.created_by.nombre}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-blue-500">{isExpanded ? '▼' : '▶'}</span>
                      </div>
                    </div>
                    {isExpanded && <DespachoDetailPanel despachoId={d.id} />}
                  </Card>
                );
              })}
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'inventario' && <InventarioTab />}

      {showExcedenteModal && (
        <PedidoExcedenteModal onClose={() => setShowExcedenteModal(false)} />
      )}

      {/* Cancel pedido confirmation */}
      <Modal isOpen={!!cancelId} onClose={() => { setCancelId(null); setCancelEstado(null); }} title="Cancelar Pedido" size="sm">
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              {['despachado_parcial', 'despachado_total'].includes(cancelEstado ?? '')
                ? 'Este pedido ya fue despachado. Al cancelarlo, los equipos despachados serán devueltos al inventario y los costos de insumos adicionales serán revertidos.'
                : 'Esta acción no se puede deshacer. El pedido quedará cancelado permanentemente.'}
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => { setCancelId(null); setCancelEstado(null); }}>Volver</Button>
            <Button variant="danger" onClick={handleCancelPedido} isLoading={cancelPedido.isPending}>
              Cancelar pedido
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Pedido Excedente Modal ────────────────────────────────────────

function PedidoExcedenteModal({ onClose }: { onClose: () => void }) {
  const { data: proyectosData } = useProjects({ estado: 'en_ejecucion', limit: 500 });
  const { data: materialesData } = useMaterials({ limit: 200 });
  const queryClient = useQueryClient();

  const [projectId, setProjectId] = useState('');
  const [materialId, setMaterialId] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [justificacion, setJustificacion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createPedido = useCreatePedido(projectId);

  const proyectos = proyectosData?.data ?? [];
  const materiales = (materialesData?.data ?? []).filter((m) => m.is_active);

  const projectOptions = proyectos.map((p) => ({ value: p.id, label: `${p.codigo} — ${p.nombre}` }));
  const materialOptions = materiales.map((m) => ({ value: m.id, label: `${m.nombre} (${m.unidad})` }));

  const canSubmit = projectId && materialId && Number(cantidad) > 0 && justificacion.trim().length >= 10;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      // 1. Create pedido excedente
      const pedido = await createPedido.mutateAsync({
        tipo: 'materiales',
        items: [{
          materialId,
          cantidad: Number(cantidad),
          justificacion: justificacion.trim(),
          esConsumibleAdicional: false,
        }],
      });

      // 2. Immediately create despacho against that pedido
      const today = new Date().toISOString().split('T')[0];
      await api.post(`/pedidos/${pedido.id}/despachos`, {
        fechaDespacho: today,
        items: pedido.items
          .filter((item) => item.material_id === materialId)
          .map((item) => ({
            pedidoItemId: item.id,
            cantidadDespachada: Number(cantidad),
          })),
      });

      // Invalidate despachos list so it refreshes
      queryClient.invalidateQueries({ queryKey: ['despachos'] });

      toast('success', 'Excedente registrado y despachado');
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      toast('error', msg ?? 'Error al registrar excedente');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Registrar Pedido Excedente"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={!canSubmit}
          >
            <AlertTriangle className="w-4 h-4 mr-1 text-amber-200" />
            Registrar y Despachar
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          Un excedente es un material solicitado más allá del límite de la Orden de Trabajo.
          Requiere justificación obligatoria.
        </div>
        <Select
          label="Proyecto"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          options={[{ value: '', label: 'Seleccionar proyecto...' }, ...projectOptions]}
        />
        <Select
          label="Material"
          value={materialId}
          onChange={(e) => setMaterialId(e.target.value)}
          options={[{ value: '', label: 'Seleccionar material...' }, ...materialOptions]}
          disabled={!projectId}
        />
        <Input
          label="Cantidad"
          type="number"
          min={0.01}
          step="any"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
          disabled={!materialId}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Justificación <span className="text-red-500">*</span>
            <span className="ml-1 text-xs text-gray-400">(mín. 10 caracteres)</span>
          </label>
          <textarea
            value={justificacion}
            onChange={(e) => setJustificacion(e.target.value)}
            placeholder="Motivo del excedente..."
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <p className="text-xs text-gray-400 mt-0.5 text-right">{justificacion.length} / 1000</p>
        </div>
      </div>
    </Modal>
  );
}

// ── Inventario Tab ────────────────────────────────────────────────

function InventarioTab() {
  const { user } = useAuthStore();
  const { data: equiposList, isLoading } = useEquipos();
  const createEquipo = useCreateEquipo();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNombre, setNewNombre] = useState('');
  const [newDescripcion, setNewDescripcion] = useState('');
  const [newStock, setNewStock] = useState(0);

  // Movement modal state
  const [movEquipoId, setMovEquipoId] = useState<string | null>(null);
  const [movTipo, setMovTipo] = useState<'salida' | 'devolucion'>('salida');
  const [ingresoEquipoId, setIngresoEquipoId] = useState<string | null>(null);
  const [bajaEquipoId, setBajaEquipoId] = useState<string | null>(null);
  const [expandedHistorial, setExpandedHistorial] = useState<string | null>(null);

  // Ubicaciones expand state
  const [expandedUbicaciones, setExpandedUbicaciones] = useState<string | null>(null);

  const canManage = ['gerente', 'operaciones', 'administrativo', 'compras'].includes(user?.role ?? '');

  const handleCreate = async () => {
    if (!newNombre.trim()) return;
    try {
      await createEquipo.mutateAsync({
        nombre: newNombre.trim(),
        descripcion: newDescripcion.trim() || null,
        stockTotal: newStock,
      });
      toast('success', 'Equipo agregado');
      setShowAddModal(false);
      setNewNombre('');
      setNewDescripcion('');
      setNewStock(0);
    } catch {
      toast('error', 'Error al crear equipo');
    }
  };

  const openMovimiento = (equipoId: string, tipo: 'salida' | 'devolucion') => {
    setMovEquipoId(equipoId);
    setMovTipo(tipo);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const equipos = equiposList ?? [];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-gray-700">Inventario de Equipos</p>
        {canManage && (
          <Button size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Agregar equipo
          </Button>
        )}
      </div>

      {equipos.length === 0 ? (
        <Card className="p-8 text-center">
          <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay equipos registrados</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {equipos.map((e) => (
            <Card key={e.id} className="overflow-hidden">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{e.nombre}</p>
                    {e.descripcion && <p className="text-xs text-gray-400">{e.descripcion}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      <span className={e.stock_disponible > 0 ? 'text-green-600' : 'text-red-500'}>
                        {e.stock_disponible}
                      </span>
                      <span className="text-gray-400"> / {e.stock_total}</span>
                    </p>
                    <p className="text-xs text-gray-400">disponible / total</p>
                    {!e.is_active && (
                      <span className="text-xs text-red-500">Inactivo</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap items-center">
                  {canManage && e.is_active && (
                    <>
                      <button
                        className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
                        onClick={() => openMovimiento(e.id, 'salida')}
                      >
                        <ArrowUpCircle className="w-3.5 h-3.5" />
                        Registrar salida
                      </button>
                      <button
                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                        onClick={() => openMovimiento(e.id, 'devolucion')}
                      >
                        <ArrowDownCircle className="w-3.5 h-3.5" />
                        Registrar devolución
                      </button>
                      <button
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        onClick={() => setIngresoEquipoId(e.id)}
                      >
                        <PlusCircle className="w-3.5 h-3.5" />
                        Agregar stock
                      </button>
                      <button
                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
                        onClick={() => setBajaEquipoId(e.id)}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Dar de baja
                      </button>
                    </>
                  )}
                  <button
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
                    onClick={() =>
                      setExpandedHistorial(expandedHistorial === e.id ? null : e.id)
                    }
                  >
                    <Clock className="w-3.5 h-3.5" />
                    {expandedHistorial === e.id ? 'Ocultar historial' : 'Ver historial'}
                  </button>
                  <button
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium ml-auto"
                    onClick={() =>
                      setExpandedUbicaciones(expandedUbicaciones === e.id ? null : e.id)
                    }
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {expandedUbicaciones === e.id ? 'Ocultar ubicaciones' : 'Ver ubicaciones'}
                  </button>
                </div>
              </div>
              {expandedUbicaciones === e.id && (
                <div className="border-t border-gray-100 bg-gray-50/50">
                  <UbicacionesPanel equipoId={e.id} />
                </div>
              )}
              {expandedHistorial === e.id && (
                <div className="border-t border-gray-100 bg-gray-50/50">
                  <HistorialMovimientosPanel equipoId={e.id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Add Equipment Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Agregar Equipo" size="sm">
        <div className="space-y-4">
          <Input label="Nombre" value={newNombre} onChange={(e) => setNewNombre(e.target.value)} placeholder="Ej: Andamio" />
          <Input label="Descripción" value={newDescripcion} onChange={(e) => setNewDescripcion(e.target.value)} placeholder="Opcional" />
          <Input label="Cantidad inicial" type="number" min={0} value={newStock || ''} onChange={(e) => setNewStock(Number(e.target.value) || 0)} />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancelar</Button>
            <Button onClick={handleCreate} isLoading={createEquipo.isPending} disabled={!newNombre.trim()}>
              Agregar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Movement Modal */}
      {movEquipoId && (
        <MovimientoModal
          equipoId={movEquipoId}
          tipo={movTipo}
          onClose={() => setMovEquipoId(null)}
        />
      )}

      {/* Ingreso Modal */}
      {ingresoEquipoId && (
        <IngresoModal
          equipoId={ingresoEquipoId}
          onClose={() => setIngresoEquipoId(null)}
        />
      )}

      {/* Baja Modal */}
      {bajaEquipoId && (
        <BajaModal
          equipoId={bajaEquipoId}
          onClose={() => setBajaEquipoId(null)}
        />
      )}
    </>
  );
}

// ── Ubicaciones Panel ─────────────────────────────────────────────

function UbicacionesPanel({ equipoId }: { equipoId: string }) {
  const { data, isLoading } = useEquipoUbicaciones(equipoId);

  if (isLoading) {
    return <p className="text-xs text-gray-400 px-4 py-2">Cargando...</p>;
  }

  if (!data || data.length === 0) {
    return <p className="text-xs text-gray-400 px-4 py-2">No hay salidas activas en proyectos.</p>;
  }

  return (
    <div className="px-4 pb-3">
      <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Ubicación actual</p>
      <div className="space-y-1">
        {data.map((u: UbicacionItem) => (
          <div
            key={u.project_id}
            className="flex items-center justify-between text-sm bg-blue-50 rounded px-3 py-1.5"
          >
            <span className="text-gray-700">
              {u.project_nombre}{' '}
              <span className="text-gray-400">({u.project_codigo})</span>
            </span>
            <span className="font-semibold text-blue-700">{u.en_proyecto} unid.</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Movimiento Modal ────────────────────────────────────────────

function MovimientoModal({ equipoId, tipo, onClose }: { equipoId: string; tipo: 'salida' | 'devolucion'; onClose: () => void }) {
  const { data: proyectos } = useProjects({ estado: 'en_ejecucion', limit: 500 });
  const { data: ubicaciones } = useEquipoUbicaciones(equipoId);
  const { data: movimientos, isLoading: loadingMovs } = useEquipoMovimientos(equipoId);
  const createMovimiento = useCreateMovimiento(equipoId);

  const [projectId, setProjectId] = useState('');
  const [cantidad, setCantidad] = useState(0);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [notas, setNotas] = useState('');

  const handleSubmit = async () => {
    if (!projectId || cantidad <= 0) return;
    try {
      await createMovimiento.mutateAsync({
        projectId,
        tipo,
        cantidad,
        fecha,
        notas: notas.trim() || null,
      });
      toast('success', tipo === 'salida' ? 'Salida registrada' : 'Devolución registrada');
      onClose();
    } catch {
      toast('error', 'Error al registrar movimiento');
    }
  };

  const projectOptions = tipo === 'devolucion'
    ? (ubicaciones ?? []).map((u) => ({ value: u.project_id, label: `${u.project_nombre} (${u.en_proyecto} unid.)` }))
    : (proyectos?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }));

  const isSalida = tipo === 'salida';

  return (
    <Modal isOpen onClose={onClose} title={isSalida ? 'Registrar Salida' : 'Registrar Devolución'} size="md">
      <div className="space-y-4">
        {isSalida && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
            Solo se muestran proyectos que están actualmente en ejecución.
          </div>
        )}
        <Select
          label="Proyecto"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          options={[{ value: '', label: 'Seleccionar proyecto...' }, ...projectOptions]}
        />
        <Input
          label="Cantidad"
          type="number"
          min={1}
          value={cantidad || ''}
          onChange={(e) => setCantidad(Number(e.target.value) || 0)}
        />
        <Input
          label="Fecha"
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Notas opcionales..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            isLoading={createMovimiento.isPending}
            disabled={!projectId || cantidad <= 0}
          >
            {isSalida ? (
              <><ArrowUpCircle className="w-4 h-4 mr-1" /> Registrar Salida</>
            ) : (
              <><ArrowDownCircle className="w-4 h-4 mr-1" /> Registrar Devolución</>
            )}
          </Button>
        </div>

        {/* Recent movements */}
        {loadingMovs ? (
          <Skeleton className="h-16 w-full" />
        ) : movimientos && movimientos.length > 0 ? (
          <div className="border-t pt-3 mt-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Movimientos recientes</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {movimientos.slice(0, 5).map((m) => (
                <div key={m.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                  <span className={m.tipo === 'salida' ? 'text-orange-600' : m.tipo === 'ingreso' ? 'text-blue-600' : m.tipo === 'baja' ? 'text-red-600' : 'text-green-600'}>
                    {m.tipo === 'salida' ? '↑ Salida' : m.tipo === 'ingreso' ? '+ Ingreso' : m.tipo === 'baja' ? '✕ Baja' : '↓ Devolución'}
                  </span>
                  <span className="text-gray-600">{m.cantidad} unid</span>
                  <span className="text-gray-400">{new Date(m.fecha).toLocaleDateString('es-EC')}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

// ── Ingreso Modal ─────────────────────────────────────────────────

function IngresoModal({ equipoId, onClose }: { equipoId: string; onClose: () => void }) {
  const createMovimiento = useCreateMovimiento(equipoId);
  const { data: movimientos, isLoading: loadingMovs } = useEquipoMovimientos(equipoId);

  const [cantidad, setCantidad] = useState(0);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [notas, setNotas] = useState('');

  const handleSubmit = async () => {
    if (cantidad <= 0) return;
    try {
      await createMovimiento.mutateAsync({
        tipo: 'ingreso',
        cantidad,
        fecha,
        notas: notas.trim() || null,
      });
      toast('success', 'Stock incrementado correctamente');
      onClose();
    } catch {
      toast('error', 'Error al registrar ingreso');
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Agregar Stock" size="sm">
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          Incrementa el stock total y disponible del equipo con unidades nuevas.
        </p>
        <Input
          label="Cantidad a agregar"
          type="number"
          min={1}
          value={cantidad || ''}
          onChange={(e) => setCantidad(Number(e.target.value) || 0)}
        />
        <Input
          label="Fecha"
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej: Compra de unidades adicionales..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            isLoading={createMovimiento.isPending}
            disabled={cantidad <= 0}
          >
            <PlusCircle className="w-4 h-4 mr-1" />
            Agregar Stock
          </Button>
        </div>

        {/* Recent movements */}
        {!loadingMovs && movimientos && movimientos.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Movimientos recientes</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {movimientos.slice(0, 5).map((m) => (
                <div key={m.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                  <span className={m.tipo === 'salida' ? 'text-orange-600' : m.tipo === 'ingreso' ? 'text-blue-600' : m.tipo === 'baja' ? 'text-red-600' : 'text-green-600'}>
                    {m.tipo === 'salida' ? '↑ Salida' : m.tipo === 'ingreso' ? '+ Ingreso' : m.tipo === 'baja' ? '✕ Baja' : '↓ Devolución'}
                  </span>
                  <span className="text-gray-600">{m.cantidad} unid</span>
                  <span className="text-gray-400">{new Date(m.fecha).toLocaleDateString('es-EC')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Baja Modal ────────────────────────────────────────────────────────

function BajaModal({ equipoId, onClose }: { equipoId: string; onClose: () => void }) {
  const createMovimiento = useCreateMovimiento(equipoId);
  const [cantidad, setCantidad] = useState(0);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (cantidad <= 0) { setError('La cantidad debe ser mayor a cero.'); return; }
    if (motivo.trim().length < 5) { setError('El motivo es requerido (mínimo 5 caracteres).'); return; }
    setError('');
    try {
      await createMovimiento.mutateAsync({
        tipo: 'baja',
        cantidad,
        fecha,
        notas: motivo.trim(),
      });
      toast('success', 'Baja registrada correctamente');
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('INSUFFICIENT_STOCK')) {
        setError('La cantidad excede el stock disponible en bodega.');
      } else {
        setError('Error al registrar la baja. Intente nuevamente.');
      }
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Dar de Baja Unidades" size="sm">
      <div className="space-y-4">
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Esta acción reduce el stock total y disponible de forma permanente. Solo se pueden dar de baja unidades que estén actualmente en bodega (no asignadas a proyectos).
        </div>
        <Input
          label="Cantidad a dar de baja"
          type="number"
          min={1}
          value={cantidad || ''}
          onChange={(e) => { setCantidad(Number(e.target.value) || 0); setError(''); }}
        />
        <Input
          label="Fecha"
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo <span className="text-red-500">*</span>
          </label>
          <textarea
            value={motivo}
            onChange={(e) => { setMotivo(e.target.value); setError(''); }}
            placeholder="Ej: Daño irreparable, robo, deterioro por uso..."
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
          />
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            isLoading={createMovimiento.isPending}
            disabled={cantidad <= 0 || motivo.trim().length < 5}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <XCircle className="w-4 h-4 mr-1" />
            Registrar Baja
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Historial Movimientos Panel ──────────────────────────────────────

const TIPO_CONFIG: Record<string, { label: string; color: string }> = {
  salida:     { label: '↑ Salida',     color: 'text-orange-600' },
  devolucion: { label: '↓ Devolución', color: 'text-green-600'  },
  ingreso:    { label: '+ Ingreso',    color: 'text-blue-600'   },
  baja:       { label: '✕ Baja',       color: 'text-red-600'    },
};

function HistorialMovimientosPanel({ equipoId }: { equipoId: string }) {
  const { data: movimientos, isLoading } = useEquipoMovimientos(equipoId);

  if (isLoading) return <p className="text-xs text-gray-400 px-4 py-2">Cargando historial...</p>;
  if (!movimientos || movimientos.length === 0) {
    return <p className="text-xs text-gray-400 px-4 py-2">No hay movimientos registrados.</p>;
  }

  return (
    <div className="px-4 py-3 space-y-1.5">
      <p className="text-xs font-semibold text-gray-500 mb-2">Historial de movimientos</p>
      <div className="max-h-56 overflow-y-auto space-y-1">
        {movimientos.map((m) => {
          const cfg = TIPO_CONFIG[m.tipo] ?? { label: m.tipo, color: 'text-gray-600' };
          return (
            <div key={m.id} className="flex items-start justify-between text-xs p-2 bg-white rounded border border-gray-100">
              <div className="flex-1 min-w-0">
                <span className={`font-medium ${cfg.color}`}>{cfg.label}</span>
                {m.notas && <p className="text-gray-500 truncate mt-0.5">{m.notas}</p>}
              </div>
              <div className="text-right ml-3 shrink-0">
                <p className="font-medium text-gray-700">{m.cantidad} unid.</p>
                <p className="text-gray-400">{new Date(m.fecha).toLocaleDateString('es-EC')}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Despacho Detail Panel ───────────────────────────────────────────

function DespachoDetailPanel({ despachoId }: { despachoId: string }) {
  const { data: detail, isLoading } = useDespachoDetail(despachoId);
  const updateItems = useUpdateDespachoItems(despachoId);
  const ordenPdf = useOrdenMaterialesPdf();

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isDirty, setIsDirty] = useState(false);

  // Initialise local quantities when detail loads
  if (detail && !isDirty && Object.keys(quantities).length === 0) {
    const initial: Record<string, number> = {};
    for (const item of detail.items) {
      initial[item.id] = item.cantidad_despachada;
    }
    setQuantities(initial);
  }

  const handleQtyChange = (itemId: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [itemId]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    const items = Object.entries(quantities).map(([itemId, cantidadDespachada]) => ({
      itemId,
      cantidadDespachada,
    }));
    try {
      await updateItems.mutateAsync(items);
      toast('success', 'Cantidades actualizadas');
      setIsDirty(false);
    } catch {
      toast('error', 'Error al actualizar cantidades');
    }
  };

  const handlePdf = () => {
    if (!detail?.pedido_id) return;
    ordenPdf.mutate({ pedidoId: detail.pedido_id });
  };

  if (isLoading) return <div className="mt-3 text-xs text-gray-400">Cargando detalle...</div>;
  if (!detail) return <div className="mt-3 text-xs text-gray-400">No se pudo cargar</div>;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3" onClick={(e) => e.stopPropagation()}>
      <p className="text-xs font-medium text-gray-500 mb-2">Items despachados:</p>
      <div className="space-y-1">
        {detail.items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-3 text-sm bg-gray-50 rounded-lg px-3 py-2">
            <span className="flex-1 text-gray-800">{item.material_nombre ?? 'Item'}</span>
            <input
              type="number"
              min={0}
              step="any"
              value={quantities[item.id] ?? item.cantidad_despachada}
              onChange={(e) => handleQtyChange(item.id, Number(e.target.value) || 0)}
              className="w-24 text-right text-sm font-semibold border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
        ))}
      </div>
      {detail.notas && <p className="text-xs text-gray-500 mt-2 italic">Notas: {detail.notas}</p>}

      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
        <Button
          size="sm"
          variant="primary"
          onClick={handleSave}
          isLoading={updateItems.isPending}
          disabled={!isDirty || updateItems.isPending}
        >
          <Save className="w-3.5 h-3.5 mr-1" />
          Guardar cambios
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={handlePdf}
          isLoading={ordenPdf.isPending}
          disabled={ordenPdf.isPending}
        >
          <FileText className="w-3.5 h-3.5 mr-1" />
          Generar PDF
        </Button>
      </div>
    </div>
  );
}
